import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs/internal/Observable';
import { take, withLatestFrom } from 'rxjs/operators';
import { IPharmacy } from 'src/app/shared/interfaces/pharmacy.interface';
import { AlertMessageService } from 'src/app/shared/services/alert-message.service';
import { GetAllLocations } from 'src/app/shared/states/inventory/inventory.actions';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { ActivityTypesEnum } from '../../../shared/interfaces/ActivityTypesEnum';
import { AlertTypesEnum } from '../../../shared/interfaces/AlertTypesEnum';
import { IUser } from '../../../shared/interfaces/user.interface';
import { UserRolesEnum } from '../../../shared/interfaces/UserRolesEnum';
import { TransactionsService } from '../../../shared/services/transactions.service';
import { UserHasPin } from '../../../shared/states/auth/auth.actions';
import { AuthState } from '../../../shared/states/auth/auth.state';
import { GetDashboardInfo } from '../../../shared/states/profile/profile.actions';
import { ProfileState } from '../../../shared/states/profile/profile.state';
import {
  CreateTransaction,
  IsBulkVerifyInProgress
} from '../../../shared/states/transactions/transactions.actions';
import { TransactionsState } from '../../../shared/states/transactions/transactions.state';
import { DialogTypeEnum } from '../../interfaces/DialogType';
import { IMultipleDrugsForm } from '../../interfaces/IMultipleDrugsForm';
import { InventoryState } from 'src/app/shared/states/inventory/inventory.state';
import { BackCountService } from 'src/app/shared/services/back-count.service';
@UntilDestroy()
@Component({
  selector: 'app-user-board',
  templateUrl: './user-board.component.html',
  styleUrls: ['./user-board.component.scss'],
})
export class UserBoardComponent implements OnInit {
  showInventoryControls: boolean;
  showSetupBlock: boolean;
  showStatisticsBlock: boolean;
  showPinDialog: boolean;
  showResetPinDialog: boolean;

  showSetPinDialog = false;
  showDispenseDialog = false;
  showReceiveDialog = false;
  showRecordLostDialog = false;
  showDamagedOrExpiredDialog = false;
  showMoveInventoryDialog: boolean;

  drugFormValue: any;

  adminRoles = [UserRolesEnum.GlobalAdmin, UserRolesEnum.CompanyAdmin, UserRolesEnum.PharmacyAdmin];

  setupProgress = {
    // hasOrder: false,
    hasInventory: false,
    hasTeammates: false,
  };

  focusPin: boolean;

  DialogTypeEnum = DialogTypeEnum;
  ActivityTypesEnum = ActivityTypesEnum;

  @Select(AuthState.activeUser) user$: Observable<IUser>;
  @Select(PharmacyState.currentPharmacy) currentPharmacy$: Observable<IPharmacy>;
  @Select(ProfileState.dashboardData) dashboardData$: Observable<any>;
  @Select(ProfileState.loading) profileLoading$: Observable<boolean>;
  @Select(TransactionsState.success) success$: Observable<boolean>;
  @Select(AuthState.userHasPinResponse) userHasPin$: Observable<boolean>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;


  constructor(
    private router: Router,
    private store: Store,
    private transactionsService: TransactionsService,
    private notificationService: AlertMessageService,
    private backCountService: BackCountService
  ) {
  }

  ngOnInit(): void {
    let pharmacy = this.store.selectSnapshot(PharmacyState.currentPharmacy);
    if (pharmacy) {
      this.store.dispatch(new GetDashboardInfo())
        .pipe(take(1), withLatestFrom(this.dashboardData$))
        .subscribe(([_, dashboardData]) => {
          if (dashboardData) {
            pharmacy = this.store.selectSnapshot(PharmacyState.currentPharmacy);
            this.setupProgress = dashboardData;
            this.showInventoryControls = dashboardData.hasInventory;
            this.showSetupBlock = !dashboardData.hasInventory ||
              (!dashboardData.hasTeammates && this.adminRoles.includes(pharmacy.role)); // + !dashboardData.hasOrder
          }
        });
      
      this.store.dispatch(new IsBulkVerifyInProgress());

      this.store.dispatch(new UserHasPin())
        .pipe(take(1), withLatestFrom(this.userHasPin$))
        .subscribe(([_, userHasPin]) => {
          this.showSetPinDialog = !userHasPin;
        });

      this.transactionsService.openReceivingDialogRequested$
        .pipe(untilDestroyed(this))
        .subscribe(() => this.receiveInventory());

      if (!this.store.selectSnapshot(InventoryState.locations)?.length) {
        this.store.dispatch(new GetAllLocations());
      }
    }
  }

  goToPage(route: string, allow: boolean): void {
    if (allow) {
      this.router.navigateByUrl(route);
    }
  }

  getTimeOfDay(): string {
    // TODO: observing all the time this code should be out of Zone
    const currentTime = new Date().getHours();

    if (currentTime >= 6 && currentTime < 12) {
      return 'morning';
    } else if (currentTime >= 12 && currentTime < 18) {
      return 'afternoon';
    } else if (currentTime >= 18 && currentTime <= 23) {
      return 'evening';
    } else {
      return 'night';
    }
  }

  closeSetPinDialog(): void {
    this.showSetPinDialog = false;
  }

  delaySetup(): void {
    this.showSetupBlock = false;
    // TODO: remove after logic implementation
    this.showInventoryControls = true;
  }

  dispense(): void {
    this.showDispenseDialog = true;
    // this.router.navigateByUrl('inventory/bulk-add');
  }

  receiveInventory(): void {
    this.showReceiveDialog = true;
  }

  reportLostOrStolen(): void {
    this.showRecordLostDialog = true;
  }

  medsDamagedOrExpired(): void {
    this.showDamagedOrExpiredDialog = true;
  }

  sendTransaction(formValue: any, pin: string): void {
    formValue.pin = +pin;
    this.store.dispatch(new CreateTransaction(Array.isArray(formValue) ? formValue : [formValue]))
      .pipe(take(1), withLatestFrom(this.success$))
      .subscribe(([_, success]) => {
        let text;
        let type;

        switch (formValue.activityType || formValue[0].activityType) {
          case ActivityTypesEnum.Dispensed:
            text = 'Dispense';
            break;
          case ActivityTypesEnum.LostStolen:
            text = 'Meds lost or stolen';
            break;
          case ActivityTypesEnum.Received:
            text = 'Receive';
            break;
          case ActivityTypesEnum.Sold:
            text = 'Sale to other provider';
            break;
          case ActivityTypesEnum.DamagedExpired:
            text = 'Meds damaged or expired';
            break;
        }

        if (success) {
          type = AlertTypesEnum.Success;
          text = text + ' activity recorded';
          this.transactionsService.refreshTransactionsTable$.next();
          this.backCountService.handleBackCountPillUpdate(formValue);
        } else {
          type = AlertTypesEnum.Error;
          text = text + ' activity failed';
        }
        this.notificationService.displayAlert(text, type);
      });
  }

  notifyMoveOperationResult(success: boolean): void {
    this.showMoveInventoryDialog = false;
    let type;
    let text;
    if (success) {
      type = AlertTypesEnum.Success;
      text = 'Move activity recorded';
      this.transactionsService.refreshTransactionsTable$.next();
    } else {
      type = AlertTypesEnum.Error;
      text = 'Move activity failed';
    }
   this.notificationService.displayAlert(text, type);
  }

  receiveDrug(formValue: any): void {
    this.showReceiveDialog = false;
    formValue.activityType = ActivityTypesEnum.Received;
    this.openPinDialog(formValue);
  }

  dispenseDrug(formValue): void {
    this.showDispenseDialog = false;
    formValue.activityType = ActivityTypesEnum.Dispensed;
    this.openPinDialog(formValue);
  }

  recordLostOrStolenDrug(formValue): void {
    this.showRecordLostDialog = false;
    formValue.activityType = ActivityTypesEnum.LostStolen;
    this.openPinDialog(formValue);
  }

  recordDamagedOrExpiredDrug(formValue): void {
    this.showDamagedOrExpiredDialog = false;
    formValue.activityType = ActivityTypesEnum.DamagedExpired;
    this.openPinDialog(formValue);
  }

  afterPinHandled({success, pin}): void {
    if (success) {
      if (Array.isArray((this.drugFormValue as IMultipleDrugsForm)?.drugs)) {
        const multipleDrugsFormData = (this.drugFormValue as IMultipleDrugsForm);
        this.drugFormValue = multipleDrugsFormData.drugs.map((drug) => { 
          return {...drug, rxNumber: multipleDrugsFormData?.rxNumber, patientName: multipleDrugsFormData?.patientName, 
              description: multipleDrugsFormData?.description, activityType: multipleDrugsFormData?.activityType, pin,
              purchaseOrderNumber: this.drugFormValue?.poNumber, wholesalerId: this.drugFormValue?.wholesaler?.id,
              invoiceNumber: this.drugFormValue?.invoiceNumber,
              shipDate: this.drugFormValue?.shipDate,
              transactionDate: this.drugFormValue?.transactionDate,
              fillNumber: this.drugFormValue?.fillNumber,
              csos: this.drugFormValue?.csos,
              supplierDetailsID: this.drugFormValue?.supplierDetailId,
              buyerDetailsID: this.drugFormValue?.buyerDetailId
            };
        });
      }
      this.sendTransaction(this.drugFormValue, pin);
      this.closePinDialog();
    }
  }

  openPinDialog(formValue: any): void {
    this.drugFormValue = formValue;
    this.showPinDialog = true;
    this.focusPin = true;
  }

  closePinDialog(): void {
    this.showPinDialog = false;
    this.focusPin = false;
  }

  openResetPinDialog(): void {
    this.focusPin = false;
    this.showPinDialog = false;
    this.showResetPinDialog = true;
  }

  closeResetPinDialog(): void {
    this.showResetPinDialog = false;
    this.showPinDialog = true;
    this.focusPin = true;
  }

  afterPinUpdated(success: boolean): void {
    // TODO: show message about pin update
    if (success) {
      this.closeResetPinDialog();
    }
  }
}
